/**
 * Stage 4: Validation
 * Comprehensive quality checks and publication readiness
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { CourseDraft, ValidationResult, ComprehensiveVocabularyEntry, AnnotatedSentence, SentenceAudio } from '../../../types/creator-workflow';
import SentenceDisplay from '../SentenceDisplay';
import UnifiedAudioPlayer from '../../UnifiedAudioPlayer';
import VocabularyModal from '../VocabularyModal';
import { SentenceEditor, VocabularyEditor, PhraseEditor, QuizEditor, FullTextTranslationEditor } from '../ContentEditor';
import { AnnotationEditor } from '../AnnotationEditor';
import { creatorWorkflowService } from '../../../services/creatorWorkflowService';
import { useAuth } from '../../../contexts/AuthContext';
import { getTranslation } from '../../../utils/getTranslation';
import './Stage4Validation.css';
import { BilingualTitle } from '../../common/BilingualTitle';
import ConfirmModal from '../../common/ConfirmModal';

import { resolveMediaUrl } from '../../../utils/mediaUrl';

// Editor state type
type EditorState =
  | { type: 'title' }
  | { type: 'sentence'; index: number }
  | { type: 'sentenceAnalysis'; index: number }
  | { type: 'vocabulary'; index: number }
  | { type: 'phrase'; index: number }
  | { type: 'quiz'; index: number }
  | { type: 'fullTextTranslation' }
  | null;

// Language code → display name (kept in sync with Stage2Parsing)
const LANGUAGE_NAMES: Record<string, string> = {
  'zh-TW': '繁體中文',
  'zh-CN': '简体中文',
  'ja': '日本語',
  'ko': '한국어',
  'th': 'ภาษาไทย',
  'vi': 'Tiếng Việt',
  'id': 'Bahasa Indonesia',
  'ms': 'Bahasa Melayu',
};

interface Stage4ValidationProps {
  draft: CourseDraft;
  onValidate: () => Promise<ValidationResult>;
  onAdvanceStage: () => Promise<void>;
  onDraftUpdate?: (draft: CourseDraft) => void;
  loading: boolean;
}

const Stage4Validation: React.FC<Stage4ValidationProps> = ({
  draft,
  onValidate,
  onAdvanceStage,
  onDraftUpdate,
  loading,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const navigate = useNavigate();
  const { user } = useAuth();
  // Draft-scoped translation language wins over the creator's UI locale,
  // so previews always match the draft's chosen output language even if
  // the creator has switched the UI to a different locale (e.g., via the
  // header LanguageSwitcher). Falls back to user's UI pref, then zh-TW.
  const userLang = draft.translation_language || user?.languagePreference || 'zh-TW';

  // Check if this is an audio-uploaded course (can edit subtitles)
  // material_source can be: audio_en_zh, audio_en, audio_zh, audio_only
  const isAudioUploaded = draft.material_source?.startsWith('audio_') ||
                          !!draft.uploaded_audio_url;

  // Editing state
  const [editingItem, setEditingItem] = useState<EditorState>(null);

  // Handle draft update from editors
  const handleDraftUpdate = (updatedDraft: CourseDraft) => {
    if (onDraftUpdate) {
      onDraftUpdate(updatedDraft);
    }
  };

  // 從 localStorage 恢復驗證結果 - 使用 draft.id 區分不同 draft
  // Fallback: 從 draft.stage_progress.validation.validation_result 取得
  const getInitialValidationResult = () => {
    const backup = localStorage.getItem(`stage5_validation_backup_${draft.id}`);
    if (backup) {
      try {
        console.log('✅ 從 localStorage 恢復 Stage 4 驗證結果 for draft:', draft.id);
        return JSON.parse(backup);
      } catch (err) {
        console.error('Failed to parse Stage 4 backup:', err);
      }
    }
    // Fallback: reconstruct from server-side validation result
    const serverResult = draft.stage_progress?.validation?.validation_result;
    if (serverResult) {
      console.log('✅ 從 stage_progress 恢復 Stage 4 驗證結果 for draft:', draft.id);
      const allPassed = serverResult.passed_checks === serverResult.total_checks;
      return {
        isValid: serverResult.is_valid,
        overallScore: serverResult.overall_score,
        passedChecks: serverResult.passed_checks,
        totalChecks: serverResult.total_checks,
        checks: {
          hasContent: { passed: allPassed, message: allPassed ? 'Content exists' : 'Missing content' },
          hasCefrLevel: { passed: allPassed, message: allPassed ? 'CEFR level set' : 'CEFR level missing' },
          hasVocabulary: { passed: allPassed, message: allPassed ? 'Vocabulary exists' : 'Vocabulary missing' },
          hasPhrases: { passed: allPassed, message: allPassed ? 'Phrases exist' : 'Phrases missing' },
          hasAudio: { passed: allPassed, message: allPassed ? 'Audio generated' : 'Audio missing' },
          contentQuality: { passed: allPassed, message: allPassed ? 'Quality check passed' : 'Quality check failed', score: serverResult.overall_score },
        },
        warnings: [],
        recommendations: [],
      };
    }
    return null;
  };

  const [validationResult, setValidationResult] = useState<ValidationResult | null>(getInitialValidationResult);
  const [validating, setValidating] = useState(false);
  // selectedSubtitleLang removed — now handled by UnifiedAudioPlayer
  const [selectedVocab, setSelectedVocab] = useState<ComprehensiveVocabularyEntry | null>(null);
  const [generatingExplanationAudio, setGeneratingExplanationAudio] = useState(false);
  const [explanationAudioResult, setExplanationAudioResult] = useState<{
    generated: number;
    skipped: number;
    errors: number;
  } | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(draft.title || '');
  const [nativeTitleValue, setNativeTitleValue] = useState(draft.title_native || '');
  const [savingTitle, setSavingTitle] = useState(false);
  const [regeneratingGraphIndex, setRegeneratingGraphIndex] = useState<number | null>(null);
  // Thumbnail state — backend fires generation on parsing completion; Stage 4
  // polls for the URL to appear and offers a force-regen via confirm modal.
  const [showThumbnailConfirm, setShowThumbnailConfirm] = useState(false);
  const [regeneratingThumbnail, setRegeneratingThumbnail] = useState(false);
  // audioRef removed — now handled by UnifiedAudioPlayer

  // Update local title fields when draft changes
  useEffect(() => {
    setTitleValue(draft.title || '');
  }, [draft.title]);

  useEffect(() => {
    setNativeTitleValue(draft.title_native || '');
  }, [draft.title_native]);

  // Thumbnail poll — backend fires thumbnail generation when parsing
  // completes (fire-and-forget). If the URL isn't on the draft yet, poll
  // every 3s for up to 60s so the image appears once ready.
  useEffect(() => {
    if (draft.thumbnail_url) return;

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 20; // 20 × 3s = 60s

    const intervalId = setInterval(async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const updated = await creatorWorkflowService.getDraft(draft.id);
        if (cancelled) return;
        if (updated.thumbnail_url) {
          clearInterval(intervalId);
          if (onDraftUpdate) onDraftUpdate(updated);
        } else if (attempts >= MAX_ATTEMPTS) {
          clearInterval(intervalId);
        }
      } catch (err) {
        console.warn('Thumbnail poll failed:', err);
        if (attempts >= MAX_ATTEMPTS) clearInterval(intervalId);
      }
    }, 3000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.id, draft.thumbnail_url]);

  // Force regenerate the thumbnail — triggered from confirm modal.
  const handleRegenerateThumbnail = async () => {
    setShowThumbnailConfirm(false);
    setRegeneratingThumbnail(true);
    try {
      await creatorWorkflowService.generateThumbnail(draft.id, { force: true });
      const updated = await creatorWorkflowService.getDraft(draft.id);
      if (onDraftUpdate) onDraftUpdate(updated);
    } catch (err) {
      console.error('Failed to regenerate thumbnail:', err);
    } finally {
      setRegeneratingThumbnail(false);
    }
  };

  // Handle title save — updates both English title and native-language title,
  // keeping the `titles` JSONB in sync so publish picks up the edited value.
  const handleTitleSave = async () => {
    const trimmedTitle = titleValue.trim();
    const trimmedNative = nativeTitleValue.trim();

    if (!trimmedTitle) {
      // English title is required — cancel the edit if the user cleared it
      setTitleValue(draft.title || '');
      setNativeTitleValue(draft.title_native || '');
      setEditingTitle(false);
      return;
    }

    const titleChanged = trimmedTitle !== draft.title;
    const nativeChanged = trimmedNative !== (draft.title_native || '');
    if (!titleChanged && !nativeChanged) {
      setEditingTitle(false);
      return;
    }

    setSavingTitle(true);
    try {
      const updates: Partial<CourseDraft> = {};
      if (titleChanged) updates.title = trimmedTitle;
      if (nativeChanged) {
        updates.title_native = trimmedNative;
        // Mirror to the titles JSONB so the downstream publish flow
        // uses the creator's edited native title rather than any stale
        // AI-suggested value.
        if (draft.translation_language) {
          updates.titles = {
            ...(draft.titles || {}),
            [draft.translation_language]: trimmedNative,
          };
        }
      }
      const updatedDraft = await creatorWorkflowService.updateDraft(draft.id, updates);
      handleDraftUpdate(updatedDraft);
      setEditingTitle(false);
    } catch (error) {
      console.error('Failed to save title:', error);
    } finally {
      setSavingTitle(false);
    }
  };

  // Handle title key press
  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setTitleValue(draft.title || '');
      setNativeTitleValue(draft.title_native || '');
      setEditingTitle(false);
    }
  };

  // Handle regenerate dependency graph
  const handleRegenerateDependencyGraph = async (index: number) => {
    setRegeneratingGraphIndex(index);
    try {
      const result = await creatorWorkflowService.regenerateDependencyGraph(draft.id, index);
      if (result.success && onDraftUpdate) {
        // Refresh draft data after regeneration
        const updatedDraft = await creatorWorkflowService.getDraft(draft.id);
        onDraftUpdate(updatedDraft);
      }
    } catch (error) {
      console.error('Failed to regenerate dependency graph:', error);
    } finally {
      setRegeneratingGraphIndex(null);
    }
  };

  // Get audio data from audio_generation stage
  const audioGenProgress = draft.stage_progress?.audio_generation;
  const segmentedAudioData = audioGenProgress?.segmented_audio_data;

  // Get merged audio URLs - prefer segmented_audio_data, fallback to direct properties, then top-level draft fields
  // For uploaded audio workflows, draft.audio_url contains the trimmed audio path
  const rawMergedAudioUrl = segmentedAudioData?.merged_audio_url || (audioGenProgress as any)?.merged_audio_url || draft.audio_url || draft.uploaded_audio_url;
  const mergedAudioUrl = rawMergedAudioUrl ? resolveMediaUrl(rawMergedAudioUrl) : undefined;
  const subtitleUrlEn = segmentedAudioData?.subtitle_url_en || (audioGenProgress as any)?.subtitle_url_en || draft.subtitle_url_en;
  const subtitleUrlNative = segmentedAudioData?.subtitle_url_native || (audioGenProgress as any)?.subtitle_url_native || draft.subtitle_url_native;
  const mergedDuration = segmentedAudioData?.merged_duration_seconds || (audioGenProgress as any)?.merged_duration_seconds || 0;

  // Get sentence audio data for individual playback
  const sentenceAudioData: SentenceAudio[] = audioGenProgress?.sentences || [];

  // 每次驗證結果變化時保存 - 使用 draft.id 區分
  useEffect(() => {
    if (validationResult) {
      localStorage.setItem(`stage5_validation_backup_${draft.id}`, JSON.stringify(validationResult));
    }
  }, [validationResult, draft.id]);

  const handleValidateClick = async () => {
    setValidating(true);
    try {
      const result = await onValidate();
      setValidationResult(result);
      // 立即保存驗證結果 - 使用 draft.id 區分
      localStorage.setItem(`stage5_validation_backup_${draft.id}`, JSON.stringify(result));
    } catch (error) {
      console.error('Validation error:', error);
    } finally {
      setValidating(false);
    }
  };

  // Handle explanation audio generation
  const handleGenerateExplanationAudio = async () => {
    setGeneratingExplanationAudio(true);
    setExplanationAudioResult(null);
    try {
      const result = await creatorWorkflowService.generateExplanationAudio(draft.id);
      setExplanationAudioResult({
        generated: result.generated,
        skipped: result.skipped,
        errors: result.errors,
      });
      // Refresh draft data after generation
      if (onDraftUpdate) {
        const updatedDraft = await creatorWorkflowService.getDraft(draft.id);
        onDraftUpdate(updatedDraft);
      }
    } catch (error) {
      console.error('Explanation audio generation error:', error);
      setExplanationAudioResult({
        generated: 0,
        skipped: 0,
        errors: 1,
      });
    } finally {
      setGeneratingExplanationAudio(false);
    }
  };

  // Count sentences with sentence_analysis and explanation_audio_url
  const countSentenceAnalysis = () => {
    if (!draft.sentences) return { withAnalysis: 0, withAudio: 0, total: 0 };
    const sentences = draft.sentences as AnnotatedSentence[];
    const withAnalysis = sentences.filter(s => s.sentence_analysis?.explanation_native).length;
    const withAudio = sentences.filter(s => s.sentence_analysis?.explanation_audio_url).length;
    return { withAnalysis, withAudio, total: sentences.length };
  };

  const sentenceAnalysisStats = countSentenceAnalysis();

  // Get explanation audio generation status from stage_progress
  const explanationAudioProgress = draft.stage_progress?.explanation_audio as {
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    total: number;
    completed: number;
    skipped: number;
    errors: number;
    started_at?: string;
    completed_at?: string;
    error?: string;
  } | undefined;

  // Poll for updates when generation is in progress
  useEffect(() => {
    if (explanationAudioProgress?.status === 'in_progress') {
      const interval = setInterval(async () => {
        try {
          const updatedDraft = await creatorWorkflowService.getDraft(draft.id);
          if (onDraftUpdate) {
            onDraftUpdate(updatedDraft);
          }
        } catch (err) {
          console.error('Failed to poll draft update:', err);
        }
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(interval);
    }
  }, [explanationAudioProgress?.status, draft.id, onDraftUpdate]);

  const getCheckIcon = (passed: boolean): string => {
    return passed ? '✅' : '❌';
  };

  const getScoreColor = (score: number): string => {
    if (score >= 85) return 'excellent';
    if (score >= 70) return 'good';
    if (score >= 50) return 'fair';
    return 'poor';
  };

  const validationProgress = draft.stage_progress.validation;
  const hasValidated = validationResult !== null || validationProgress?.completed;

  // Check if comprehensive learning content exists
  const hasComprehensiveContent = draft.sentences && draft.sentences.length > 0;
  const vocabulary = (draft.vocabulary || []) as ComprehensiveVocabularyEntry[];

  return (
    <div className="stage-4-validation">
      <header className="stage-header">
        <h2>{t('creator:workflow.stage4.title')}</h2>
        <p>{t('creator:workflow.stage4.description')}</p>
      </header>

      {/* Course Title Section */}
      <div className="course-title-section">
        <div className="course-title-container">
          {editingTitle ? (
            <div className="title-edit-form">
              <div className="title-edit-field">
                <label className="title-edit-label">
                  {t('creator:workflow.stage2.englishTitle')}
                </label>
                <input
                  type="text"
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  autoFocus
                  className="title-input"
                  placeholder={t('creator:workflow.stage4.courseTitlePlaceholder')}
                  disabled={savingTitle}
                />
              </div>
              {draft.translation_language && (
                <div className="title-edit-field">
                  <label className="title-edit-label">
                    {t('creator:workflow.stage2.nativeTitle')}
                    {' '}
                    ({LANGUAGE_NAMES[draft.translation_language] || draft.translation_language})
                  </label>
                  <input
                    type="text"
                    value={nativeTitleValue}
                    onChange={(e) => setNativeTitleValue(e.target.value)}
                    onKeyDown={handleTitleKeyDown}
                    className="title-input title-input-native"
                    placeholder={t('creator:workflow.stage2.nativeTitle')}
                    disabled={savingTitle}
                  />
                </div>
              )}
              <div className="title-edit-actions">
                <button
                  className="btn-title-save"
                  onClick={handleTitleSave}
                  disabled={savingTitle}
                >
                  {savingTitle ? t('common:saving') : t('common:save')}
                </button>
                <button
                  className="btn-title-cancel"
                  onClick={() => {
                    setTitleValue(draft.title || '');
                    setNativeTitleValue(draft.title_native || '');
                    setEditingTitle(false);
                  }}
                  disabled={savingTitle}
                >
                  {t('common:cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="title-display">
              <BilingualTitle
                title={draft.title || ''}
                titles={draft.titles}
                fallback={t('common:untitled')}
                variant="header"
                as="h1"
                className="course-title"
              />
              <button
                className="btn-edit-title"
                onClick={() => setEditingTitle(true)}
                title={t('creator:workflow.stage4.editCourseTitle')}
              >
                {t('creator:workflow.stage4.editTitle')}
              </button>
            </div>
          )}
        </div>
        <div className="course-meta">
          <span className="meta-item">
            <strong>{t('creator:workflow.stage4.cefrLabel')}</strong> {draft.cefr_level || t('creator:workflow.stage4.notSet')}
          </span>
          <span className="meta-item">
            <strong>{t('creator:workflow.stage4.contentTypeLabel')}</strong> {draft.content_type || t('creator:workflow.stage4.notSet')}
          </span>
          {draft.actual_word_count && (
            <span className="meta-item">
              <strong>{t('creator:workflow.stage4.wordCountLabel')}</strong> {draft.actual_word_count}
            </span>
          )}
        </div>
      </div>

      {/* Course Thumbnail Section */}
      <div className="course-thumbnail-section">
        <div className="section-header">
          <div className="section-header-top">
            <h3>{t('creator:workflow.stage4.thumbnail')}</h3>
            {draft.thumbnail_url && (
              <button
                className="btn-regenerate-thumbnail"
                onClick={() => setShowThumbnailConfirm(true)}
                disabled={regeneratingThumbnail}
              >
                {regeneratingThumbnail
                  ? t('course:manage.generating')
                  : t('course:manage.regenerateThumbnail')}
              </button>
            )}
          </div>
        </div>
        <div className="thumbnail-container">
          {draft.thumbnail_url ? (
            <img
              src={resolveMediaUrl(draft.thumbnail_url)}
              alt={draft.title || t('common:untitled')}
              className="thumbnail-image"
            />
          ) : (
            <div className="thumbnail-placeholder">
              <div className="thumbnail-spinner" aria-hidden="true" />
              <p>{t('course:manage.generating')}</p>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showThumbnailConfirm}
        title={t('course:manage.regenerateThumbnailTitle')}
        message={t('course:manage.regenerateThumbnailWarning')}
        confirmText={t('course:manage.regenerateThumbnail')}
        onConfirm={handleRegenerateThumbnail}
        onCancel={() => setShowThumbnailConfirm(false)}
        loading={regeneratingThumbnail}
      />

      {/* Complete Audio Player Section */}
      {mergedAudioUrl && (
        <div className="audio-preview-section">
          <div className="section-header">
            <div className="section-header-top">
              <h3>{t('creator:workflow.stage4.audioPreview')}</h3>
              {isAudioUploaded && (
                <button
                  className="btn-edit-subtitles"
                  onClick={() => navigate(`/creator/subtitle-editor/${draft.id}`)}
                  title={t('creator:workflow.stage4.editSubtitlesTiming')}
                >
                  {t('creator:workflow.stage4.editSubtitles')}
                </button>
              )}
            </div>
            {/* description removed — self-evident from context */}
          </div>

          <UnifiedAudioPlayer
            audioUrl={mergedAudioUrl}
            subtitleUrlEn={subtitleUrlEn ? resolveMediaUrl(subtitleUrlEn) : undefined}
            subtitleUrlNative={subtitleUrlNative ? resolveMediaUrl(subtitleUrlNative) : undefined}
          />
        </div>
      )}

      {/* Learning Content Preview Section */}
      {hasComprehensiveContent && (
        <div className="learning-content-section">
          <div className="section-header">
            <div className="section-header-top">
              <h3>{t('creator:workflow.stage4.learningContentPreview')}</h3>
              {/* Explanation Audio Generation Status */}
              {sentenceAnalysisStats.withAnalysis > 0 && (
                <div className="explanation-audio-controls">
                  {/* Show progress when auto-generating */}
                  {explanationAudioProgress?.status === 'in_progress' && (
                    <div className="explanation-audio-progress">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${((explanationAudioProgress.completed || 0) / (explanationAudioProgress.total || 1)) * 100}%` }}
                        />
                      </div>
                      <span className="progress-text">
                        {t('creator:workflow.stage4.autoGenerating', {
                          done: explanationAudioProgress.completed || 0,
                          total: explanationAudioProgress.total || 0,
                        })}
                      </span>
                    </div>
                  )}
                  {/* Show button when not auto-generating */}
                  {explanationAudioProgress?.status !== 'in_progress' && (
                    <>
                      <button
                        className={`btn-generate-explanation ${sentenceAnalysisStats.withAudio === sentenceAnalysisStats.withAnalysis ? 'completed' : ''}`}
                        onClick={handleGenerateExplanationAudio}
                        disabled={generatingExplanationAudio || loading}
                        title={t('creator:workflow.stage4.explanationAudioGenerateTitle', { count: sentenceAnalysisStats.withAnalysis })}
                      >
                        {generatingExplanationAudio ? (
                          t('creator:workflow.stage4.generatingExplanationAudio')
                        ) : sentenceAnalysisStats.withAudio === sentenceAnalysisStats.withAnalysis ? (
                          t('creator:workflow.stage4.explanationAudioGenerated', { done: sentenceAnalysisStats.withAudio, total: sentenceAnalysisStats.withAnalysis })
                        ) : sentenceAnalysisStats.withAudio > 0 ? (
                          t('creator:workflow.stage4.explanationAudioRegenerate', { done: sentenceAnalysisStats.withAudio, total: sentenceAnalysisStats.withAnalysis })
                        ) : explanationAudioProgress?.status === 'pending' ? (
                          t('creator:workflow.stage4.explanationAudioPending')
                        ) : explanationAudioProgress?.status === 'failed' ? (
                          t('creator:workflow.stage4.explanationAudioFailed')
                        ) : (
                          t('creator:workflow.stage4.explanationAudioGenerate', { count: sentenceAnalysisStats.withAnalysis })
                        )}
                      </button>
                      {explanationAudioResult && (
                        <span className="explanation-audio-result">
                          {explanationAudioResult.generated > 0 && <span className="success">{t('creator:workflow.stage4.generatedCount', { count: explanationAudioResult.generated })}</span>}
                          {explanationAudioResult.skipped > 0 && <span className="warning">{t('creator:workflow.stage4.skippedCount', { count: explanationAudioResult.skipped })}</span>}
                          {explanationAudioResult.errors > 0 && <span className="error">{t('creator:workflow.stage4.errorsCount', { count: explanationAudioResult.errors })}</span>}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            <p className="section-description">
              {t('creator:workflow.stage4.learningContentDesc')}
              {sentenceAnalysisStats.withAnalysis > 0 && (
                <span className="analysis-hint">
                  {' '}{t('creator:workflow.stage4.sentenceAnalysisHint', { count: sentenceAnalysisStats.withAnalysis })}
                </span>
              )}
            </p>
          </div>

          <div className="sentences-container">
            {(draft.sentences ?? []).map((sentence: AnnotatedSentence, index: number) => {
              // Find matching audio for this sentence and enrich the sentence object
              const sentenceAudio = sentenceAudioData.find(
                (audio) => audio.index === index || audio.text === sentence.text
              );
              // Enrich sentence with audio URL if available
              const enrichedSentence: AnnotatedSentence = sentenceAudio
                ? { ...sentence, audio_url: sentenceAudio.audioUrl }
                : sentence;
              return (
                <div key={index} className="sentence-item-wrapper">
                  <SentenceDisplay
                    sentence={enrichedSentence}
                    index={index}
                    vocabulary={vocabulary}
                    translationLanguage={userLang}
                    onRegenerateDependencyGraph={handleRegenerateDependencyGraph}
                    isRegenerating={regeneratingGraphIndex === index}
                    additionalActions={
                      <div className="sentence-action-buttons">
                        <button
                          className="btn-edit-item"
                          onClick={() => setEditingItem({ type: 'sentence', index })}
                          title={t('creator:workflow.stage4.editSentenceTitle')}
                        >
                          ✏️ {t('creator:workflow.stage4.editSentence')}
                        </button>
                        {enrichedSentence.sentence_analysis && (
                          <>
                            <span className="action-separator">|</span>
                            <button
                              className="btn-edit-item analysis"
                              onClick={() => setEditingItem({ type: 'sentenceAnalysis', index })}
                              title={t('creator:workflow.stage4.editSentenceAnalysisTitle')}
                            >
                              🎙️ {t('creator:workflow.stage4.editExplanation')}
                            </button>
                          </>
                        )}
                      </div>
                    }
                  />
                </div>
              );
            })}
          </div>

          {vocabulary.length > 0 && (
            <div className="vocabulary-section">
              <div className="vocabulary-header">
                <h4>{t('creator:workflow.stage4.vocabularyListCount', { count: vocabulary.length })}</h4>
                <p>
                  {t('creator:workflow.stage4.vocabularyDesc')}
                </p>
              </div>
              <div className="vocabulary-cards">
                {vocabulary.map((vocab, idx) => (
                  <div
                    key={idx}
                    className="vocab-card clickable"
                  >
                    <button
                      className="btn-edit-vocab"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingItem({ type: 'vocabulary', index: idx });
                      }}
                      title={t('creator:workflow.stage4.editVocabularyTitle')}
                    >
                      ✏️
                    </button>
                    <div
                      className="vocab-card-content"
                      onClick={() => setSelectedVocab(vocab)}
                      title={t('creator:workflow.stage4.vocabDetailsTitle')}
                    >
                    <div className="vocab-card-header">
                      <span className="vocab-word">{vocab.word}</span>
                      <span className="vocab-pos">{vocab.pos}</span>
                      <span className={`vocab-difficulty diff-${vocab.difficulty <= 3 ? 'easy' : vocab.difficulty <= 6 ? 'medium' : 'hard'}`}>
                        {vocab.difficulty <= 2 ? 'A1' : vocab.difficulty <= 3 ? 'A2' : vocab.difficulty <= 5 ? 'B1' : vocab.difficulty <= 6 ? 'B2' : vocab.difficulty <= 8 ? 'C1' : 'C2'}
                      </span>
                    </div>
                    {vocab.pronunciation && (
                      <div className="vocab-pronunciation">
                        /{vocab.pronunciation}/
                      </div>
                    )}
                    <div className="vocab-definitions">
                      <p className="vocab-def-en">{vocab.general_definition_en}</p>
                      <p className="vocab-def-zh">{getTranslation((vocab as any).general_definitions, userLang, vocab.general_definition)}</p>
                      {vocab.definition_in_context && (
                        <p className="vocab-context">
                          <strong>{t('creator:workflow.stage4.contextDefLabel')}</strong> {getTranslation((vocab as any).definition_in_contexts, userLang, vocab.definition_in_context)}
                        </p>
                      )}
                    </div>
                    {vocab.example_sentences && vocab.example_sentences.length > 0 && (
                      <div className="vocab-examples">
                        <strong>{t('creator:workflow.stage4.exampleSentencesLabel')}</strong>
                        {vocab.example_sentences.slice(0, 2).map((ex, exIdx) => (
                          <div key={exIdx} className="vocab-example">
                            <p className="example-en">{ex.sentence_en}</p>
                            <p className="example-zh">{getTranslation((ex as any).sentence_natives, userLang, ex.sentence_native)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {vocab.synonyms && vocab.synonyms.length > 0 && (
                      <div className="vocab-related">
                        <span className="related-label">{t('creator:workflow.stage4.synonymsLabel')}</span>
                        {vocab.synonyms.slice(0, 3).map((syn, sIdx) => (
                          <span key={sIdx} className="related-tag synonym">{syn}</span>
                        ))}
                      </div>
                    )}
                    {vocab.collocations && vocab.collocations.length > 0 && (
                      <div className="vocab-related">
                        <span className="related-label">{t('creator:workflow.stage4.collocationsLabel')}</span>
                        {vocab.collocations.slice(0, 3).map((col, cIdx) => (
                          <span key={cIdx} className="related-tag collocation">{col}</span>
                        ))}
                      </div>
                    )}
                    {vocab.vocabulary_quiz && (
                      <div className="vocab-quiz-indicator">
                        {t('creator:workflow.stage4.hasQuizIndicator')}
                      </div>
                    )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Phrases Section */}
          {draft.phrases && draft.phrases.length > 0 && (
            <div className="phrases-section">
              <div className="section-header-with-edit">
                <h4>{t('creator:workflow.stage4.phrasesCount', { count: draft.phrases.length })}</h4>
              </div>
              <div className="phrases-list">
                {draft.phrases.map((phrase: any, idx: number) => (
                  <div key={idx} className="phrase-item">
                    <button
                      className="btn-edit-item-inline"
                      onClick={() => setEditingItem({ type: 'phrase', index: idx })}
                      title={t('creator:workflow.stage4.editPhraseTitle')}
                    >
                      ✏️
                    </button>
                    <div className="phrase-content">
                      <span className="phrase-text">{phrase.text}</span>
                      <span className={`phrase-type type-${phrase.type}`}>{t(`creator:workflow.stage4.phraseTypes.${phrase.type}`, { defaultValue: phrase.type })}</span>
                    </div>
                    <p className="phrase-def-en">{phrase.definition_en}</p>
                    <p className="phrase-def-zh">{getTranslation((phrase as any).definition_natives, userLang, phrase.definition_native)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quiz Questions Section */}
          {draft.quiz_questions && draft.quiz_questions.length > 0 && (
            <div className="quiz-section">
              <div className="section-header-with-edit">
                <h4>{t('creator:workflow.stage4.quizQuestionsCount', { count: draft.quiz_questions.length })}</h4>
              </div>
              <div className="quiz-list">
                {draft.quiz_questions.map((quiz: any, idx: number) => (
                  <div key={idx} className="quiz-item">
                    <button
                      className="btn-edit-item-inline"
                      onClick={() => setEditingItem({ type: 'quiz', index: idx })}
                      title={t('creator:workflow.stage4.editQuizTitle')}
                    >
                      ✏️
                    </button>
                    <div className="quiz-header">
                      <span className="quiz-number">Q{idx + 1}</span>
                      <span className={`quiz-type type-${quiz.type}`}>{quiz.type?.replace('_', ' ')}</span>
                      {quiz.difficulty && (
                        <span className={`quiz-difficulty diff-${quiz.difficulty}`}>{quiz.difficulty}</span>
                      )}
                    </div>
                    <p className="quiz-question-en">{quiz.question_en}</p>
                    <p className="quiz-question-zh">{getTranslation((quiz as any).question_natives, userLang, quiz.question_native)}</p>
                    {quiz.options && (
                      <div className="quiz-options">
                        {quiz.options.map((opt: any, optIdx: number) => (
                          <div key={optIdx} className={`quiz-option ${opt.text === quiz.correct_answer ? 'correct' : ''}`}>
                            {String.fromCharCode(65 + optIdx)}. {opt.text}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Full Text Translation Section */}
          {draft.full_text_translation?.translation && (
            <div className="translation-section">
              <div className="section-header-with-edit">
                <h4>{t('creator:workflow.stage4.fullTextTranslation')}</h4>
                <button
                  className="btn-edit-item"
                  onClick={() => setEditingItem({ type: 'fullTextTranslation' })}
                  title={t('creator:workflow.stage4.editTranslationTitle')}
                >
                  {t('creator:workflow.stage4.editTranslation')}
                </button>
              </div>
              <div className="translation-content">
                <p>{getTranslation((draft as any).full_text_translations, userLang, draft.full_text_translation.translation)}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {!hasValidated && (
        <div className="validation-prompt">
          <div className="info-box">
            <h3>{t('creator:workflow.stage4.readyForValidation')}</h3>
            <p>
              {t('creator:workflow.stage4.validationDesc')}
            </p>
            <ul>
              <li>{t('creator:workflow.stage4.validationCheck1')}</li>
              <li>{t('creator:workflow.stage4.validationCheck2')}</li>
              <li>{t('creator:workflow.stage4.validationCheck3')}</li>
              <li>{t('creator:workflow.stage4.validationCheck4')}</li>
              <li>{t('creator:workflow.stage4.validationCheck5')}</li>
              <li>{t('creator:workflow.stage4.validationCheck6')}</li>
              <li>{t('creator:workflow.stage4.validationCheck7')}</li>
            </ul>
            <p className="validation-note">
              {t('creator:workflow.stage4.validationMinScore')}
            </p>
          </div>

          <div className="form-actions">
            <button
              onClick={handleValidateClick}
              className="btn-primary"
              disabled={validating || loading}
            >
              {validating ? t('common:processing') : t('creator:workflow.stage4.runValidation')}
            </button>
          </div>
        </div>
      )}

      {validationResult && (
        <div className="validation-results">
          <div className={`overall-score ${getScoreColor(validationResult.overallScore)}`}>
            <div className="score-circle">
              <span className="score-value">{validationResult.overallScore}</span>
              <span className="score-label">/100</span>
            </div>
            <div className="score-status">
              {validationResult.isValid ? (
                <>
                  <h3>🎉 {t('creator:workflow.stage4.readyToPublish')}</h3>
                  <p>{t('creator:workflow.stage4.readyToPublishDesc')}</p>
                </>
              ) : (
                <>
                  <h3>⚠️ {t('creator:workflow.stage4.needsImprovements')}</h3>
                  <p>{t('creator:workflow.stage4.needsImprovementsDesc')}</p>
                </>
              )}
            </div>
          </div>

          <div className="checks-section">
            <h4>{t('creator:workflow.stage4.qualityChecks')}</h4>
            <div className="checks-grid">
              <div className="check-item">
                <span className="check-icon">{getCheckIcon(validationResult.checks.hasContent.passed)}</span>
                <div className="check-details">
                  <strong>{t('creator:workflow.stage4.checkContent')}</strong>
                  <span>{validationResult.checks.hasContent.message}</span>
                </div>
              </div>

              <div className="check-item">
                <span className="check-icon">{getCheckIcon(validationResult.checks.hasCefrLevel.passed)}</span>
                <div className="check-details">
                  <strong>{t('creator:workflow.stage4.checkCefrLevel')}</strong>
                  <span>{validationResult.checks.hasCefrLevel.message}</span>
                </div>
              </div>

              <div className="check-item">
                <span className="check-icon">{getCheckIcon(validationResult.checks.hasVocabulary.passed)}</span>
                <div className="check-details">
                  <strong>{t('creator:workflow.stage4.checkVocabulary')}</strong>
                  <span>{validationResult.checks.hasVocabulary.message}</span>
                </div>
              </div>

              <div className="check-item">
                <span className="check-icon">{getCheckIcon(validationResult.checks.hasPhrases.passed)}</span>
                <div className="check-details">
                  <strong>{t('creator:workflow.stage4.checkPhraseChunks')}</strong>
                  <span>{validationResult.checks.hasPhrases.message}</span>
                </div>
              </div>

              <div className="check-item">
                <span className="check-icon">{getCheckIcon(validationResult.checks.hasAudio.passed)}</span>
                <div className="check-details">
                  <strong>{t('creator:workflow.stage4.checkFullAudio')}</strong>
                  <span>{validationResult.checks.hasAudio.message}</span>
                </div>
              </div>

              <div className="check-item">
                <span className="check-icon">{getCheckIcon(validationResult.checks.contentQuality.passed)}</span>
                <div className="check-details">
                  <strong>{t('creator:workflow.stage4.checkContentQuality')}</strong>
                  <span>{validationResult.checks.contentQuality.message}</span>
                  {validationResult.checks.contentQuality.score && (
                    <span className="check-score">
                      {t('creator:workflow.stage4.checkScore')} {validationResult.checks.contentQuality.score}/100
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {validationResult.warnings && validationResult.warnings.length > 0 && (
            <div className="warnings-section">
              <h4>⚠️ {t('creator:workflow.stage4.warnings', { count: validationResult.warnings.length })}</h4>
              <ul>
                {validationResult.warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="form-actions">
            {validationResult.isValid ? (
              <>
                <button
                  onClick={handleValidateClick}
                  className="btn-secondary"
                  disabled={validating || loading}
                >
                  {t('creator:workflow.stage4.reValidate')}
                </button>
                <button
                  className="btn-success"
                  onClick={onAdvanceStage}
                  disabled={loading}
                >
                  {t('creator:workflow.stage4.proceedToPublishing')}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleValidateClick}
                  className="btn-secondary"
                  disabled={validating || loading}
                >
                  {t('creator:workflow.stage4.reValidate')}
                </button>
                <button
                  className="btn-disabled"
                  disabled
                >
                  {t('creator:workflow.stage4.cannotProceed', { score: validationResult.overallScore })}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {validating && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>{t('common:processing')}</p>
        </div>
      )}

      {/* Vocabulary Modal - shows dictionary phonetic and audio */}
      {selectedVocab && (
        <VocabularyModal
          vocabulary={selectedVocab}
          onClose={() => setSelectedVocab(null)}
        />
      )}

      {/* Editor Modals */}
      {editingItem?.type === 'sentence' && draft.sentences && (
        <SentenceEditor
          draftId={draft.id}
          sentence={draft.sentences[editingItem.index]}
          index={editingItem.index}
          onUpdate={handleDraftUpdate}
          onClose={() => setEditingItem(null)}
        />
      )}

      {editingItem?.type === 'vocabulary' && vocabulary.length > 0 && (
        <VocabularyEditor
          draftId={draft.id}
          vocab={vocabulary[editingItem.index]}
          index={editingItem.index}
          onUpdate={handleDraftUpdate}
          onClose={() => setEditingItem(null)}
        />
      )}

      {editingItem?.type === 'phrase' && draft.phrases && (
        <PhraseEditor
          draftId={draft.id}
          phrase={draft.phrases[editingItem.index]}
          index={editingItem.index}
          onUpdate={handleDraftUpdate}
          onClose={() => setEditingItem(null)}
        />
      )}

      {editingItem?.type === 'quiz' && draft.quiz_questions && (
        <QuizEditor
          draftId={draft.id}
          question={draft.quiz_questions[editingItem.index]}
          index={editingItem.index}
          onUpdate={handleDraftUpdate}
          onClose={() => setEditingItem(null)}
        />
      )}

      {editingItem?.type === 'fullTextTranslation' && (
        <FullTextTranslationEditor
          draftId={draft.id}
          currentTranslation={draft.full_text_translation?.translation || ''}
          onUpdate={handleDraftUpdate}
          onClose={() => setEditingItem(null)}
        />
      )}

      {editingItem?.type === 'sentenceAnalysis' && draft.sentences && (
        <AnnotationEditor
          sentenceText={(draft.sentences[editingItem.index] as AnnotatedSentence).text}
          sentenceIndex={editingItem.index}
          sentenceAnalysis={(draft.sentences[editingItem.index] as AnnotatedSentence).sentence_analysis || null}
          audioDuration={(draft.sentences[editingItem.index] as AnnotatedSentence).sentence_analysis?.explanation_duration}
          onSave={async (annotations, explanation) => {
            await creatorWorkflowService.updateSentenceAnalysis(draft.id, editingItem.index, {
              annotations,
              explanation_native: explanation,
            });
            const updatedDraft = await creatorWorkflowService.getDraft(draft.id);
            handleDraftUpdate(updatedDraft);
          }}
          onClose={() => setEditingItem(null)}
          onRegenerateAudio={async () => {
            await creatorWorkflowService.regenerateSentenceExplanationAudio(draft.id, editingItem.index);
            const updatedDraft = await creatorWorkflowService.getDraft(draft.id);
            handleDraftUpdate(updatedDraft);
          }}
        />
      )}
    </div>
  );
};

export default Stage4Validation;
